import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { chamar } from '@/services/geo-engine/client';
import { ErroDaGeoEngine } from '@/services/geo-engine/errors';
const CHAVE_DE_ARMAZENAMENTO = 'geo-engine.sessaoAdministrativa.token';
const Contexto = createContext(null);
function lerTokenArmazenado() {
    try {
        return window.sessionStorage.getItem(CHAVE_DE_ARMAZENAMENTO);
    }
    catch {
        return null;
    }
}
function gravarTokenArmazenado(token) {
    try {
        if (token === null) {
            window.sessionStorage.removeItem(CHAVE_DE_ARMAZENAMENTO);
        }
        else {
            window.sessionStorage.setItem(CHAVE_DE_ARMAZENAMENTO, token);
        }
    }
    catch {
        // Armazenamento indisponível (modo privado, por exemplo): a sessão simplesmente não persiste.
    }
}
/**
 * Sessão administrativa humana, distinta da credencial técnica do Viewer (`SessaoProvider`).
 * Mantém o token administrativo e valida/restaura a sessão junto a `/auth/me`.
 */
export function SessaoAdminProvider({ children }) {
    const [sessao, setSessao] = useState(undefined);
    const [entrando, setEntrando] = useState(false);
    const [erroDeLogin, setErroDeLogin] = useState(null);
    useEffect(() => {
        const tokenArmazenado = lerTokenArmazenado();
        if (tokenArmazenado === null) {
            setSessao(null);
            return;
        }
        let cancelado = false;
        void (async () => {
            try {
                const { dados } = await chamar({
                    metodo: 'GET',
                    caminho: '/auth/me',
                    autenticada: false,
                    credencialAdministrativa: tokenArmazenado,
                });
                if (!cancelado) {
                    setSessao({ token: tokenArmazenado, username: dados.username });
                }
            }
            catch {
                gravarTokenArmazenado(null);
                if (!cancelado) {
                    setSessao(null);
                }
            }
        })();
        return () => {
            cancelado = true;
        };
    }, []);
    const entrar = useCallback(async (username, password) => {
        setEntrando(true);
        setErroDeLogin(null);
        try {
            const { dados } = await chamar({
                metodo: 'POST',
                caminho: '/auth/login',
                autenticada: false,
                corpo: { username, password },
            });
            gravarTokenArmazenado(dados.token);
            setSessao({ token: dados.token, username: dados.username });
            return true;
        }
        catch (falha) {
            setErroDeLogin(falha instanceof ErroDaGeoEngine && falha.registro.status === 401 ? 'credenciais' : 'conexao');
            return false;
        }
        finally {
            setEntrando(false);
        }
    }, []);
    const sair = useCallback(() => {
        gravarTokenArmazenado(null);
        setSessao(null);
    }, []);
    const valor = useMemo(() => ({ sessao, entrando, erroDeLogin, entrar, sair }), [sessao, entrando, erroDeLogin, entrar, sair]);
    return _jsx(Contexto.Provider, { value: valor, children: children });
}
export function useSessaoAdmin() {
    const contexto = useContext(Contexto);
    if (contexto === null) {
        throw new Error('useSessaoAdmin exige SessaoAdminProvider acima na árvore');
    }
    return contexto;
}
